import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import type { Theme } from '@mui/material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import type { Supplier } from '../../types/procurement';

interface SupplierProgressBoardProps {
  suppliers: Supplier[];
  onUpdateStatus: (supplierId: string, newStatus: Supplier['status']) => void;
  onSendEmail: (supplier: Supplier, type: 'REJECTED' | 'SHORTLISTED') => void;
}

const COLUMNS: { id: Supplier['status']; label: string; getColor: (theme: Theme) => string }[] = [
  { id: 'SUBMITTED', label: 'Submitted', getColor: (theme) => theme.palette.grey[500] },
  { id: 'EVALUATING', label: 'Under Review', getColor: (theme) => theme.palette.warning.main },
  { id: 'REJECTED', label: 'Rejected', getColor: (theme) => theme.palette.error.main },
  { id: 'SHORTLISTED', label: 'Shortlisted', getColor: (theme) => theme.palette.info.main },
  { id: 'AWARDED', label: 'Awarded', getColor: (theme) => theme.palette.success.main },
];

const SupplierProgressBoard: React.FC<SupplierProgressBoardProps> = ({
  suppliers,
  onUpdateStatus,
  onSendEmail,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeSupplier, setActiveSupplier] = useState<Supplier | null>(null);

  // Track sent emails locally by supplier ID to prevent duplicate sending in the current session
  const [sentEmails, setSentEmails] = useState<Record<string, 'REJECTED' | 'SHORTLISTED'>>({});

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, supplier: Supplier) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveSupplier(supplier);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveSupplier(null);
  };

  const handleStatusChange = (newStatus: Supplier['status']) => {
    if (activeSupplier) {
      onUpdateStatus(activeSupplier.id, newStatus);
    }
    handleMenuClose();
  };

  const handleSendEmail = (supplier: Supplier, type: 'REJECTED' | 'SHORTLISTED') => {
    onSendEmail(supplier, type);
    setSentEmails((prev) => ({ ...prev, [supplier.id]: type }));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        overflowX: 'auto',
        pb: 2,
        minHeight: '60vh',
      }}
    >
      {COLUMNS.map((col) => {
        const colSuppliers = suppliers.filter((s) => s.status === col.id);
        return (
          <Box
            key={col.id}
            sx={{
              minWidth: 220,
              flex: 1,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `3px solid ${col.getColor(theme)}`,
                pb: 0.5,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {col.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                {colSuppliers.length}
              </Typography>
            </Box>

            {colSuppliers.map((supplier) => {
              const hasSentEmail = sentEmails[supplier.id] === col.id;

              return (
                <Card key={supplier.id} sx={{ boxShadow: 1, borderRadius: 1 }}>
                  <CardContent sx={{ p: 1.5, pb: '4px !important' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, pr: 1 }}>
                        {supplier.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, supplier)}
                        sx={{ p: 0, mt: -0.5, mr: -0.5 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {supplier.submissionDate && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        {new Date(supplier.submissionDate).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>

                  {/* Action Buttons area */}
                  {(col.id === 'REJECTED' || col.id === 'SHORTLISTED') && (
                    <CardActions sx={{ px: 1, pb: 1, pt: 0, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        disabled={hasSentEmail}
                        startIcon={hasSentEmail ? <CheckCircleIcon /> : <EmailIcon />}
                        color={col.id === 'REJECTED' ? 'error' : 'success'}
                        onClick={() =>
                          handleSendEmail(supplier, col.id as 'REJECTED' | 'SHORTLISTED')
                        }
                        sx={{ textTransform: 'none', py: 0 }}
                      >
                        {hasSentEmail ? 'Sent' : col.id === 'REJECTED' ? 'Reject' : 'Notify'}
                      </Button>
                    </CardActions>
                  )}
                </Card>
              );
            })}
          </Box>
        );
      })}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary">
            Move to...
          </Typography>
        </MenuItem>
        {COLUMNS.map((col) => (
          <MenuItem
            key={col.id}
            onClick={() => handleStatusChange(col.id)}
            selected={activeSupplier?.status === col.id}
            disabled={activeSupplier?.status === col.id}
            sx={{ py: 1 }}
          >
            {col.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default SupplierProgressBoard;
